import axios from "axios";
import { API_BASE_URL, getAuthToken } from "../artistStudio/shared";

// API helpers for Rights & Legal on the website (/v1/rights-claim/...): file a copyright notice or an ownership
// dispute, answer a notice with a counter-notice, add evidence and follow a case. Needs a signed-in user.
const url = (path) => `${API_BASE_URL}/v1/rights-claim/${path}`;
const config = (extra = {}) => ({ headers: { token: getAuthToken(), "Content-Type": "application/json" }, timeout: 20000, ...extra });
const result = (response) => response.data?.result;

export const rightsClaimApi = {
  fileNotice: (body) => axios.post(url("notice"), body, config()).then(result),
  fileDispute: (body) => axios.post(url("dispute"), body, config()).then(result),
  // Report audio (any signed-in user): body { track_id, reason, details? }; reason is explicit_not_marked, misleading, offensive, spam or other.
  reportSound: (body) => axios.post(url("report"), body, config()).then(result),
  counterNotice: (body) => axios.post(url("counter-notice"), body, config()).then(result),
  mine: (params = {}) => axios.get(url("mine"), config({ params })).then(result),
  detail: (id) => axios.get(url(encodeURIComponent(id)), config()).then(result),
  addEvidence: (id, body) => axios.post(url(`${encodeURIComponent(id)}/evidence`), body, config()).then(result),
  // The artist answers an ownership claim, once (FSD 6.8.3). body: { decision: "dispute", reason, evidence? } or { decision: "accept", outcome: "takedown" } or { decision: "accept", outcome: "revenue_share", share_percent }.
  respond: (id, body) => axios.post(url(`${encodeURIComponent(id)}/respond`), body, config()).then(result),
};

// Strikes on the signed-in artist's account (/v1/artist-strike/mine). Read only: the platform issues and removes them.
export const strikeApi = {
  mine: (params = {}) => axios.get(`${API_BASE_URL}/v1/artist-strike/mine`, config({ params })).then(result),
};

// Field -> message map from a 400 VALIDATION_ERROR, so each message can sit next to its input.
export const fieldErrors = (error) => (error?.response?.data?.code === "VALIDATION_ERROR" ? error.response.data.errors || {} : {});

export const claimErrorText = (error, fallback = "Something went wrong. Please try again.") => {
  const response = error?.response;
  if (!response) return "We couldn't reach the server. Check your connection and try again.";
  if (response.status === 401) return "Your session has expired. Please log in again.";
  return response.data?.message || fallback;
};

export const STATUS_INFO = {
  open: { label: "Received", tone: "blue" },
  under_review: { label: "Under review", tone: "amber" },
  awaiting_info: { label: "Information needed", tone: "red" },
  evidence_required: { label: "Evidence needed", tone: "red" },
  action_required: { label: "Action required", tone: "red" },
  on_hold: { label: "On hold", tone: "gray" },
  escalated: { label: "Escalated", tone: "amber" },
  resolved: { label: "Resolved", tone: "green" },
  rejected: { label: "Rejected", tone: "gray" },
};

// What the Claims Received table shows for an ownership claim (FSD 6.8.3).
export const DISPLAY_STATUS_INFO = {
  pending: { label: "Pending", tone: "amber" },
  accepted: { label: "Accepted", tone: "green" },
  disputed: { label: "Disputed", tone: "blue" },
  resolved: { label: "Resolved", tone: "gray" },
};

export const TONE_CLASS = {
  gray: "bg-gray-100 text-gray-700",
  blue: "bg-blue-50 text-blue-800",
  amber: "bg-amber-50 text-amber-800",
  green: "bg-emerald-50 text-emerald-800",
  red: "bg-red-50 text-red-700",
};

export const REPORT_REASONS = [
  { value: "explicit_not_marked", label: "Explicit content not marked", hint: "It has strong language or adult content but is not marked explicit." },
  { value: "misleading", label: "Misleading", hint: "The title, cover or description does not match the sound." },
  { value: "offensive", label: "Offensive", hint: "Hateful, harassing or harmful." },
  { value: "spam", label: "Spam", hint: "Repeated, unwanted or promotional uploads." },
  { value: "other", label: "Something else", hint: "Tell us what is wrong." },
];

export const DISPUTE_TYPES = [
  { value: "ownership", label: "Ownership", hint: "I own the music or recording." },
  { value: "rights", label: "Rights", hint: "I hold rights in it (publishing, label, distribution)." },
  { value: "authorization", label: "Authorization", hint: "The artist did not authorise this upload." },
];

export const formatDay = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
};
export const formatDayTime = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
};
